import Can from "common/components/Can";
import { useAppDispatch, useAppSelector } from "common/hooks/storeHooks";
import { AclKeys, AclValues } from "common/records/User";
import { CommonActions } from "common/reducers/common.actions";
import { currentUserOrgDescendantsCount } from "common/selectors/org.selectors";
import React from "react";
import { useLocation } from "react-router";
import { CustomLink } from "common/components/CustomLink";
import { currentUserPermissions } from "common/selectors/user.selectors";
import { occPayProviderActive } from "common/selectors/occPay.selectors";
import { RootState } from "common/reducers";

export interface HamburgerItemType {
  name: string;
  icon: string;
  path: string;
  closeOnClick: boolean;
  permissions?: [AclKeys, AclValues];
  // Controls whether the item should be shown or not
  showSelector?: (state: RootState) => boolean;
}

interface HamburgerItemProps {
  item: HamburgerItemType;
}

export const HamburgerItem: React.FC<HamburgerItemProps> = ({ item }) => {
  // Hooks
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  // Selectors
  const userOrgCount = useAppSelector(currentUserOrgDescendantsCount);
  const permissions = useAppSelector(currentUserPermissions);
  const tokenAuthorized = useAppSelector(occPayProviderActive);
  const showSelectorValue = useAppSelector((state) => item.showSelector && item.showSelector(state));

  if (item.path === "/orgs" && userOrgCount === 0) return null;

  // Constants
  const activePath = pathname.split("/")[1];
  // External links should not be active
  let active = !item.path.startsWith("http") ? activePath === item.path.split("/")[1] : false;

  // distinguish between /orgs && /orgs/1/info
  if (activePath === "orgs" && active) {
    if (pathname.length > 5) {
      active = item.path.length > 5;
    } else {
      active = item.path === "/orgs";
    }
  }

  // Methods
  const closeOnClick = () => {
    if (item.closeOnClick) {
      dispatch(CommonActions.setHamburgerMenu(false));
    }
  };

  const showFinanceTab = () => {
    const paymentReport = permissions?.get("payment")?.includes("payment_report");
    const depositReport = permissions?.get("payment")?.includes("deposit_report");
    const chargeBackReport = permissions?.get("payment")?.includes("chargeback_report");
    const hasPermissions = paymentReport || depositReport || chargeBackReport;

    return hasPermissions && tokenAuthorized;
  };

  const element = (
    <div>
      <CustomLink href={item.path} onClick={closeOnClick}>
        <div
          className={`py-sm flex items-center text-secondary-900 text-md rounded leading-5 text-white ${
            active ? "px-sm bg-secondary-800" : ""
          }`}>
          <span className="material-symbols-outlined mr-sm text-[18px]">{item.icon}</span>
          <span>{item.name}</span>
        </div>
      </CustomLink>
    </div>
  );
  if (item.path === "/finance" && !showFinanceTab()) {
    return null;
  }
  if (item.permissions) {
    return (
      <Can resource={item.permissions[0]} permission={item.permissions[1]}>
        {element}
      </Can>
    );
  } else if (item.showSelector && !showSelectorValue) {
    return null;
  } else return element;
};
